import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AutomationsApi } from '@src/services/automations/automations-api';
import { CanonicalItemDTO } from '@src/services/automations/modules/v1/canonicals/dto/canonicals.get.response.dto';
import { BrainApi } from '@src/services/brain/brain-api';
import { BrainResourceResponseDTO } from '@src/services/brain/modules/v1/resources/dto/brain-resources.get.response.dto';
import { LutienApi } from '@src/services/lutien/lutien-api';
import { HubApi } from '@src/services/hub/hub-api';
import { BrainResourceGetDefinitionsByResourceIdResponseDTO } from '@src/services/brain/modules/v1/resources/dto/brain-resources.getDefinitionsByResourceId.response.dto';
import { GetStatusResponseDTO } from '@src/services/brain/modules/v1/auth/dto/brain-auth.getStatus.response.dto';

type Canonical = CanonicalItemDTO;

interface CanonicalsContextType {
  // State
  canonicals: Canonical[];
  selectedCanonical: Canonical | null;
  selectedCanonicalOrganizationId: string | null;
  publishedResourceDetails: BrainResourceResponseDTO | null;
  resourceDetails: BrainResourceResponseDTO | null;
  organizationDefinitions: BrainResourceGetDefinitionsByResourceIdResponseDTO['items'] | null;
  organizationDefinitionsError: { code?: number; message: string } | null;
  organizationAuthStatus: GetStatusResponseDTO | null;
  extensionSubscriptionsOrgIds: string[] | null;
  organizationsMap: Record<string, string>;
  isLoading: boolean;
  isLoadingResource: boolean;
  isLoadingPublishedResource: boolean;
  isLoadingOrganizationDefinitions: boolean;
  isLoadingOrganizationAuthStatus: boolean;
  isCreating: boolean;
  isEditing: boolean;
  hasMoreDefinitions: boolean;
  currentDefinitionsPage: number;

  // Handlers
  handleCanonicalClick: (canonical: Canonical) => void;
  handleCanonicalOrganizationClick: (organizationId: string) => void;
  handleCloseDetails: () => void;
  handleCloseOrganizationDetails: () => void;
  handleCreateNew: () => void;
  handleEdit: (canonical: Canonical) => void;
  handleSave: (canonicalData: Partial<Canonical>) => Promise<void>;
  handleDelete: (canonical: Canonical) => Promise<void>;
  handleCancel: () => void;
  handleLoadMoreDefinitions: () => void;
  handleTexSubscriptions: (organizationId: string, type: 'add' | 'remove') => void;
  handlePublishDefinition: (definitionId: string) => Promise<void>;
  refetchResourceDetails: () => Promise<void>;
  refetchOrganizationDefinitions: () => Promise<void>;
}

const CanonicalsContext = createContext<CanonicalsContextType | undefined>(undefined);

export const useCanonicalsContext = () => {
  const context = useContext(CanonicalsContext);
  if (!context) {
    throw new Error('useCanonicalsContext must be used within CanonicalsProvider');
  }
  return context;
};

interface CanonicalsProviderProps {
  children: ReactNode;
}

export const CanonicalsProvider: React.FC<CanonicalsProviderProps> = ({ children }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [canonicals, setCanonicals] = useState<Canonical[]>([]);
  const [selectedCanonical, setSelectedCanonical] = useState<Canonical | null>(null);
  const [selectedCanonicalOrganizationId, setSelectedCanonicalOrganizationId] = useState<string | null>(null);
  const [resourceDetails, setResourceDetails] = useState<BrainResourceResponseDTO | null>(null);
  const [publishedResourceDetails, setPublishedResourceDetails] = useState<BrainResourceResponseDTO | null>(null);
  const [organizationDefinitions, setOrganizationDefinitions] = useState<
    BrainResourceGetDefinitionsByResourceIdResponseDTO['items'] | null
  >(null);
  const [organizationDefinitionsError, setOrganizationDefinitionsError] = useState<{
    code?: number;
    message: string;
  } | null>(null);
  const [organizationAuthStatus, setOrganizationAuthStatus] = useState<GetStatusResponseDTO | null>(null);
  const [extensionSubscriptionsOrgIds, setExtensionSubscriptionsOrgIds] = useState<string[] | null>(null);
  const [isLoadingResource, setIsLoadingResource] = useState(false);
  const [isLoadingPublishedResource, setIsLoadingPublishedResource] = useState(false);
  const [isLoadingOrganizationDefinitions, setIsLoadingOrganizationDefinitions] = useState(false);
  const [isLoadingOrganizationAuthStatus, setIsLoadingOrganizationAuthStatus] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [organizationsMap, setOrganizationsMap] = useState<Record<string, string>>({});
  const [currentDefinitionsPage, setCurrentDefinitionsPage] = useState(1);
  const [hasMoreDefinitions, setHasMoreDefinitions] = useState(false);

  const selectedCanonicalId = searchParams.get('canonicalId');
  const selectedCanonicalOrganizationIdFromSearch = searchParams.get('organizationId');

  const fetchCanonicals = async () => {
    setIsLoading(true);
    try {
      const automationsApi = new AutomationsApi();
      const result = await automationsApi.v1.canonicals.getList({
        params: {
          currentPage: 1,
          limit: 100,
        },
      });
      setCanonicals(result.items);
    } catch {
      setCanonicals([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchOrganizationAuthStatus = async (organizationId: string) => {
    setIsLoadingOrganizationAuthStatus(true);
    setOrganizationAuthStatus(null);
    try {
      const brainApi = new BrainApi();
      const result = await brainApi.v1.auth.getStatus({
        params: {
          organizationId,
          key: 'DEFAULT',
        },
        settings: {
          maxRetries: 1,
          retryDelay: 500,
          delayType: 'linear',
        },
      });
      setOrganizationAuthStatus(result);
    } catch (error) {
      console.error('Failed to fetch organization auth status:', error);
      setOrganizationAuthStatus(null);
    } finally {
      setIsLoadingOrganizationAuthStatus(false);
    }
  };

  const fetchOrganizationDefinitions = async (page: number = 1, append: boolean = false) => {
    if (!selectedCanonical?.resourceId || !selectedCanonicalOrganizationIdFromSearch) return;

    setIsLoadingOrganizationDefinitions(true);
    setOrganizationDefinitionsError(null);
    try {
      const brainApi = new BrainApi();
      const result = await brainApi.v1.resources.getDefinitionsByResourceId({
        resourceId: selectedCanonical.resourceId,
        params: {
          currentPage: page,
          limit: 10,
          organizationId: selectedCanonicalOrganizationIdFromSearch,
        },
        settings: {
          maxRetries: 0,
          retryDelay: 0,
          delayType: 'linear',
        },
      });

      if (append) {
        setOrganizationDefinitions((prev) =>
          [...(prev || []), ...result.items].sort((a, b) => (a.version < b.version ? 1 : 0)),
        );
      } else {
        setOrganizationDefinitions(result.items.sort((a, b) => (a.version < b.version ? 1 : 0)));
      }

      setHasMoreDefinitions(page < result.totalPages);
      setCurrentDefinitionsPage(page);
    } catch (error) {
      console.error('Failed to fetch organization definitions:', error.message);
      const errorCode = error?.response?.status || error?.status;
      const errorMessage =
        errorCode === 403 || error.message.includes('403')
          ? 'Acesso Negado: Você não tem permissão para visualizar as definições desta organização.'
          : 'Erro ao carregar definições. Tente novamente mais tarde.';

      setOrganizationDefinitionsError({
        code: errorCode,
        message: errorMessage,
      });

      if (!append) {
        setOrganizationDefinitions([]);
      }
    } finally {
      setIsLoadingOrganizationDefinitions(false);
    }
  };

  const fetchResourceDetails = async (resourceId: string) => {
    setIsLoadingResource(true);
    setResourceDetails(null);
    try {
      const brainApi = new BrainApi();
      const result = await brainApi.v1.resources.getById({
        id: resourceId,
        params: {
          includeCurrentDefinitions: true,
          onlyPublished: false,
        },
      });
      setResourceDetails(result);
    } catch (error) {
      console.error('Failed to fetch resource details:', error);
      setResourceDetails(null);
    } finally {
      setIsLoadingResource(false);
    }
  };

  const fetchPublishedResourceDetails = async (resourceId: string) => {
    setIsLoadingPublishedResource(true);
    setPublishedResourceDetails(null);
    try {
      const brainApi = new BrainApi();
      const result = await brainApi.v1.resources.getById({
        id: resourceId,
        params: {
          includeCurrentDefinitions: true,
          onlyPublished: true,
        },
      });
      setPublishedResourceDetails(result);
    } catch (error) {
      console.error('Failed to fetch resource details:', error);
      setPublishedResourceDetails(null);
    } finally {
      setIsLoadingPublishedResource(false);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const lutienApi = new LutienApi();
      const organizations = await lutienApi.v1.organizations.get();

      const orgMap: Record<string, string> = {};
      organizations.forEach((org) => {
        orgMap[org.id] = org.fantasyName;
      });

      setOrganizationsMap(orgMap);
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
      setOrganizationsMap({});
    }
  };

  const fetchTexSubscriptionsOrgIds = async (texId: string) => {
    try {
      const hubApi = new HubApi();
      const extension = await hubApi.v1.extensions.getByIdOrName({
        idOrName: texId,
      });
      setExtensionSubscriptionsOrgIds(extension.subscribedOrganizationIds || []);
    } catch (error) {
      console.error('Failed to fetch tex subscriptions:', error);
      setExtensionSubscriptionsOrgIds([]);
    }
  };

  useEffect(() => {
    fetchCanonicals();
    fetchOrganizations();
  }, []);

  useEffect(() => {
    if (selectedCanonicalId) {
      const canonical = canonicals.find((c) => c.id === selectedCanonicalId);
      setSelectedCanonical(canonical || null);

      if (canonical?.resourceId) {
        let resourceId: string | null = canonical.resourceId;

        if (resourceId) {
          fetchResourceDetails(resourceId);
          fetchPublishedResourceDetails(resourceId);
        } else {
          setResourceDetails(null);
          setPublishedResourceDetails(null);
        }
      } else {
        setResourceDetails(null);
        setPublishedResourceDetails(null);
      }
    } else {
      setSelectedCanonical(null);
      setResourceDetails(null);
      setPublishedResourceDetails(null);
      setExtensionSubscriptionsOrgIds(null);
    }
  }, [selectedCanonicalId, canonicals]);

  useEffect(() => {
    if (selectedCanonicalOrganizationIdFromSearch) {
      setSelectedCanonicalOrganizationId(selectedCanonicalOrganizationIdFromSearch);
      setOrganizationDefinitions(null);
      setOrganizationDefinitionsError(null);
      setOrganizationAuthStatus(null);
      setCurrentDefinitionsPage(1);
      setHasMoreDefinitions(false);
      fetchOrganizationDefinitions(1, false);
      fetchOrganizationAuthStatus(selectedCanonicalOrganizationIdFromSearch);
    } else {
      setSelectedCanonicalOrganizationId(null);
      setOrganizationDefinitions(null);
      setOrganizationDefinitionsError(null);
      setOrganizationAuthStatus(null);
    }
  }, [selectedCanonicalOrganizationIdFromSearch, selectedCanonical]);

  useEffect(() => {
    if (selectedCanonical?.tarkenExtensionId) {
      fetchTexSubscriptionsOrgIds(selectedCanonical.tarkenExtensionId);
    }
  }, [selectedCanonical]);

  const handleTexSubscriptions = async (organizationId: string, type: 'add' | 'remove') => {
    if (!selectedCanonical) return;
    const hubApiService = new HubApi();

    if (type === 'add')
      await hubApiService.v1.extensions.postSubscriptionById({
        id: selectedCanonical.tarkenExtensionId,
        body: {
          organizationId,
        },
      });
    else
      await hubApiService.v1.extensions.deleteSubscriptionById({
        id: selectedCanonical.tarkenExtensionId,
        organizationId,
      });

    await fetchTexSubscriptionsOrgIds(selectedCanonical.tarkenExtensionId);
  };

  const handleCanonicalOrganizationClick = (organizationId: string) => {
    if (Boolean(selectedCanonical)) {
      setSearchParams({ canonicalId: selectedCanonical.id, organizationId });
    }
  };

  const refetchResourceDetails = async () => {
    if (selectedCanonical?.resourceId) {
      await fetchResourceDetails(selectedCanonical.resourceId);
    }
  };

  const refetchOrganizationDefinitions = async () => {
    if (selectedCanonical?.resourceId && selectedCanonicalOrganizationIdFromSearch) {
      await Promise.all([
        fetchOrganizationDefinitions(1, false),
        fetchPublishedResourceDetails(selectedCanonical.resourceId),
      ]);
    }
  };

  const handlePublishDefinition = async (definitionId: string) => {
    if (!selectedCanonical?.resourceId) return;
    const brainApi = new BrainApi();

    await brainApi.v1.resources.postPublishDefinitionById({
      resourceId: selectedCanonical.resourceId,
      resourceDefinitionId: definitionId,
    });

    await Promise.all([fetchResourceDetails(selectedCanonical.resourceId), refetchOrganizationDefinitions()]);
  };

  const handleCanonicalClick = (canonical: Canonical) => {
    setSearchParams({ canonicalId: canonical.id });
  };

  const handleCloseDetails = () => {
    setSearchParams({});
  };

  const handleCloseOrganizationDetails = () => {
    if (selectedCanonicalId) {
      setSearchParams({ canonicalId: selectedCanonicalId });
    }
  };

  const handleCreateNew = () => {
    setIsCreating(true);
    setSelectedCanonical(null);
    setSearchParams({});
  };

  const handleEdit = (canonical: Canonical) => {
    setIsEditing(true);
    setSelectedCanonical(canonical);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleSave = async (canonicalData: Partial<Canonical>) => {
    setIsCreating(false);
    setIsEditing(false);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleDelete = async (canonical: Canonical) => {
    // TODO: Implement delete logic
  };

  const handleCancel = () => {
    setIsCreating(false);
    setIsEditing(false);
    if (!selectedCanonicalId) {
      setSelectedCanonical(null);
    }
  };

  const handleLoadMoreDefinitions = () => {
    if (hasMoreDefinitions && !isLoadingOrganizationDefinitions) {
      fetchOrganizationDefinitions(currentDefinitionsPage + 1, true);
    }
  };

  const value: CanonicalsContextType = {
    canonicals,
    selectedCanonical,
    selectedCanonicalOrganizationId,
    resourceDetails,
    publishedResourceDetails,
    organizationDefinitions,
    organizationDefinitionsError,
    organizationAuthStatus,
    extensionSubscriptionsOrgIds,
    organizationsMap,
    isLoading,
    isLoadingResource,
    isLoadingPublishedResource,
    isLoadingOrganizationDefinitions,
    isLoadingOrganizationAuthStatus,
    isCreating,
    isEditing,
    hasMoreDefinitions,
    currentDefinitionsPage,
    handleCanonicalClick,
    handleCanonicalOrganizationClick,
    handleCloseDetails,
    handleCloseOrganizationDetails,
    handleCreateNew,
    handleEdit,
    handleSave,
    handleDelete,
    handleCancel,
    handleLoadMoreDefinitions,
    handleTexSubscriptions,
    handlePublishDefinition,
    refetchResourceDetails,
    refetchOrganizationDefinitions,
  };

  return <CanonicalsContext.Provider value={value}>{children}</CanonicalsContext.Provider>;
};
